import { getDocs, query, where } from "firebase/firestore";
import { pointageCollection, userCollection } from "./firebase";

const getPointage = async (codeAffaire, month) => {
  try {
    const date = getDateForPointage(month);

    const pointageQuery = query(
      pointageCollection,
      where("verified", "==", true),
      where("date", ">=", new Date(date.start)),
      where("date", "<=", new Date(date.end))
    );

    const pointageSnapshot = await getDocs(pointageQuery);
    const allPointages = pointageSnapshot.docs.map((doc) => doc.data());

    const filteredResults = allPointages.filter((pointage) =>
      pointage.normalWork?.some((nw) => nw.codeAffaire === codeAffaire) ||
      pointage.extraWork?.some((ew) => ew.codeAffaire === codeAffaire)
    );

    const uniqueResults = [];
    const uniquePointageIds = new Set();

    filteredResults.forEach((result) => {
      if (!uniquePointageIds.has(result.pointage_id)) {
        uniqueResults.push(result);
        uniquePointageIds.add(result.pointage_id);
      }
    });

    return uniqueResults;
  } catch (error) {
    console.error("Error in getPointage:", error);
    return [];
  }
};

const getAgents = async (Ids) => {
  try {
    if (!Ids.length) return [];

    const agentsQuery = query(
      userCollection,
      where("user_id", "in", Ids),
      where("role", "==", "Agent")
    );

    const querySnapshot = await getDocs(agentsQuery);
    return querySnapshot.docs.map((doc) => doc.data());
  } catch (error) {
    console.error("Error in getAgents:", error);
    return [];
  }
};

const processData = async (data, Agents, codeAffaire) => {
  try {
    let totalMonthHours = 0;
    let totalMonthPayment = 0;
    const res = [];

    for (const ag of Agents) {
      let {
        fullName,
        group_id,
        pricePerDay
      } = ag;

      let Normalwork = 0,
        Extrawork = 0,
        HolidayNormalwork = 0,
        HolidayExtrawork = 0,
        SundayNormalwork = 0,
        SundayExtrawork = 0,
        totalTrasportPrice = 0,
        totalDeplacementPrice = 0;

      for (const poi of data) {
        if (ag.user_id === poi.user_id) {
          const normalWorkObject = poi.normalWork.find((obj) => obj.codeAffaire === codeAffaire);
          const extraWorkObject = poi.extraWork.find((obj) => obj.codeAffaire === codeAffaire);

          if (poi.holiday) {
            if (normalWorkObject) HolidayNormalwork += Number(normalWorkObject.hours);
            if (extraWorkObject) HolidayExtrawork += Number(extraWorkObject.hours);
          } else if (isSunday(poi.date)) {
            if (normalWorkObject) SundayNormalwork += Number(normalWorkObject.hours);
            if (extraWorkObject) SundayExtrawork += Number(extraWorkObject.hours);
          } else {
            if (normalWorkObject) Normalwork += Number(normalWorkObject.hours);
            if (extraWorkObject) Extrawork += Number(extraWorkObject.hours);
          }

          totalTrasportPrice += Number(poi.transportPrice);
          totalDeplacementPrice += Number(poi.deplacementPrice);
        }
      }

      const pricePerHour = (pricePerDay === "-" || !pricePerDay) ? 0 : Number(pricePerDay) / 8;
      const holidaysPrice = (HolidayNormalwork + HolidayExtrawork) * 2 * pricePerHour;
      const SundayPrice = (SundayNormalwork + SundayExtrawork) * 1.5 * pricePerHour;
      const NormalPrice = (Normalwork + Extrawork) * pricePerHour;

      const totalNormalwork = HolidayNormalwork + SundayNormalwork + Normalwork;
      const totalExtrawork = HolidayExtrawork + SundayExtrawork + Extrawork;

      const price = holidaysPrice + SundayPrice + NormalPrice + totalTrasportPrice + totalDeplacementPrice;

      res.push({
        fullName,
        group_id,
        totalNormalwork,
        totalExtrawork,
        totalTrasportPrice,
        totalDeplacementPrice,
        totalHours: totalNormalwork + totalExtrawork,
        price: price.toFixed(2),
      });

      totalMonthPayment += price;
      totalMonthHours += totalExtrawork + totalNormalwork;
    }

    res.sort((a, b) => b.totalHours - a.totalHours);

    return {
      totalMonthHours,
      totalMonthPayment: totalMonthPayment.toFixed(2),
      table: res,
    };
  } catch (error) {
    console.error("Error in processData:", error);
    return { totalMonthHours: 0, totalMonthPayment: "0.00", table: [] };
  }
};

const gettheData = async (codeAffaire, month) => {
  try {
    const data = await getPointage(codeAffaire, month);

    const Ids = [...new Set(data.map((d) => d.user_id))];
    if (!Ids.length) return { table: [], totalMonthHours: "0", totalMonthPayment: "0.00" };

    const Agents = await getAgents(Ids);
    return await processData(data, Agents, codeAffaire);
  } catch (error) {
    console.error("Error in gettheData:", error);
    return { table: [], totalMonthHours: "0", totalMonthPayment: "0.00" };
  }
};

const getData = async (codeAffaire) => {
  try {
    let totalYearHours = 0;
    let totalYearPayment = 0;

    const months = getMonthsFromJan2024();
    const dataForYear = [];

    for (const month of months) {
      const dataForMonth = await gettheData(codeAffaire, month);
      if (dataForMonth.totalMonthHours !== "0") {
        dataForYear.push({ month, data: dataForMonth });
        totalYearHours += Number(dataForMonth.totalMonthHours);
        totalYearPayment += Number(dataForMonth.totalMonthPayment);
      }
    }

    return { dataForYear, yearSumup: { totalYearHours, totalYearPayment } };
  } catch (error) {
    console.error("Error in getData:", error);
    return { dataForYear: [], yearSumup: { totalYearHours: 0, totalYearPayment: 0 } };
  }
};

function isSunday(timestamp) {
  const poiTimestamp = timestamp.seconds * 1000 + Math.round(timestamp.nanoseconds / 1e6);
  return new Date(poiTimestamp).getDay() === 0;
}

function getDateForPointage(selectedMonth) {
  const [year, month] = selectedMonth.split("-");
  const startDate = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const endDate = new Date(year, month, 0, 23, 59, 59, 999);
  return { start: startDate, end: endDate };
}

const getMonthsFromJan2024 = () => {
  const months = [];
  const startYear = 2024;
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  for (let year = startYear; year <= currentYear; year++) {
    for (let month = 1; month <= (year === currentYear ? currentMonth : 12); month++) {
      months.push(`${year}-${month.toString().padStart(2, "0")}`);
    }
  }

  return months;
};

export { gettheData, getData };
